import React, { useState, useCallback, useContext } from 'react'
import { useAuthFlow } from '../../Context/AuthFlowContext';
import { useUser } from '../../Context/UserContext';
import './Register.css'
import { UrlContext } from '../../Context/UrlContext';
import { useNavigate } from 'react-router-dom';
import { useNav } from '../../Context/NavContext';
import GoogleAuthButton from './GoogleAuthButton';

function Register() {

   const { apiBaseUrl } = useContext(UrlContext);

  const { toggleAuthFlow } = useAuthFlow();
  const { loginWithGoogle } = useUser();
  const { setActiveItem } = useNav();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Client-side validation
  const validateInput = useCallback(() => {
    // Name validation
    if (formData.name.length < 2 || formData.name.length > 50) {
      setError('Name must be between 2 and 50 characters');
      return false;
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Password validation (at least 8 chars, 1 uppercase, 1 lowercase, 1 number)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number');
      return false;
    }

    return true;
  }, [formData]);

  // Sanitize input
  const sanitizeInput = useCallback((input) => {
    return input.trim().replace(/[<>]/g, '');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: sanitizeInput(value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Prevent multiple submissions
    if (loading) return;

    // Validate input
    if (!validateInput()) {
      return;
    }

    setLoading(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(`${apiBaseUrl}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        signal: controller.signal,
        credentials: 'include' // For cookies if needed
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      setSuccess('Registration successful! Redirecting...');
      
      // Clear sensitive data
      setFormData({
        name: '',
        email: '',
        password: ''
      });

      // Show success message for 3 seconds then redirect
      setTimeout(() => {
        setSuccess('');
        toggleAuthFlow();
      }, 3000);

    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = useCallback(async (credential) => {
    if (loading) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const result = await loginWithGoogle(credential);

      if (result.success) {
        setSuccess('Google sign-up successful! Redirecting...');
        setTimeout(() => {
          if (result.user.role === 'admin') {
            navigate('/admin');
          } else {
            setActiveItem('COLLECTION');
          }
          toggleAuthFlow();
        }, 800);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Google sign-up error:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, loginWithGoogle, navigate, setActiveItem, toggleAuthFlow]);

  return (
    <div className='register_container'>
      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="reg_title">
          <h2>Register</h2>
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <input 
          type="text" 
          name="name"
          placeholder='Name'
          value={formData.name}
          onChange={handleChange}
          maxLength={50}
          pattern="[A-Za-z0-9 ]+"
          title="Name can only contain letters, numbers and spaces"
          required
        />
        <input 
          type="email" 
          name="email"
          placeholder='Email'
          value={formData.email}
          onChange={handleChange}
          maxLength={100}
          required
        />
        <input 
          type="password" 
          name="password"
          placeholder='Password'
          value={formData.password}
          onChange={handleChange}
          minLength={8}
          maxLength={50}
          required
          autoComplete="new-password"
        />
        <div className="forget">
          <a href="#" onClick={(e) => e.preventDefault()}>forget your password?</a>
          <a href="#" onClick={(e) => {e.preventDefault(); toggleAuthFlow();}}>login</a>
        </div>
        <div className="reg_btn">
          <button 
            type="submit" 
            disabled={loading}
            aria-busy={loading}
          >
            {loading ? 'Registering...' : 'Register'}
          </button>
        </div>
        <GoogleAuthButton onCredential={handleGoogleRegister} disabled={loading} />
      </form>
    </div>
  )
}

export default Register
